import { useEffect, useRef, useState } from 'react';

const isMobile = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(max-width: 767px)').matches || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || ''));

/**
 * Reveal an element when it scrolls into view.
 * Returns [ref, isVisible].
 *
 * On mobile we trigger ~140px BEFORE the element enters the viewport (positive
 * bottom rootMargin) so by the time the user actually sees it, the animation is
 * already mid-flight — never a "pop in".
 */
export default function useReveal({ threshold, rootMargin, once = true } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // SSR / older browsers
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    // Initial check — if element is already on-screen at mount, reveal immediately.
    const rect = node.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh && rect.bottom > 0) {
      setVisible(true);
      if (once) return;
    }

    const mobile = isMobile();
    const obsThreshold = threshold ?? 0;
    // Positive bottom margin → fire BEFORE element enters viewport.
    const obsRootMargin = rootMargin ?? (mobile ? '0px 0px 140px 0px' : '0px 0px 80px 0px');

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            // requestAnimationFrame ensures the style flip happens on the next paint,
            // giving the browser a clean frame to transition from.
            requestAnimationFrame(() => setVisible(true));
            if (once) obs.unobserve(e.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold: obsThreshold, rootMargin: obsRootMargin }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, rootMargin, once]);

  return [ref, visible];
}
